import React from 'react';
import { Controller } from 'react-hook-form';
import { Grid, Typography } from '@material-ui/core';
import { addHours } from 'date-fns';

import TimeInput from 'components/TimeInput';
import { getDate } from 'helpers/getDate';
import { validateDatesOverlapping, validateEndAfterStart } from '../../../Kiosk/helpers/validate';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { convertToArr } from 'helpers/_date-helpers';

const PARTS = [
  ['hour', 'Hours'],
  ['minute', 'Minutes'],
  ['amPm', 'AmPm'],
];

const TimeSelectForm = ({
  classes,
  control,
  label,
  name,
  index,
  errors,
  setError,
  clearErrors,
  values,
  setValue,
}) => {
  const { dateFormat } = useDateSettingsFormat();
  const getTimeFormatHook = useTimeSettingsFormat();

  const parts = getTimeFormatHook().is12Format ? PARTS : PARTS.slice(0, 2);

  // `values` is stale until the next render, so this returns them with the written time applied.
  const setTime = (field, time, source = values) =>
    parts.reduce((next, [part, suffix]) => {
      const key = `${field}${suffix}`;
      setValue(`${key}[${index}]`, time[part]);
      return { ...next, [key]: next[key].map((value, i) => (i === index ? time[part] : value)) };
    }, source);

  const changeEndDate = ({ hour, minute, amPm }) => {
    const [nextEndDate] = convertToArr(
      addHours(
        getDate(values.startDate[index], hour, minute, amPm, getTimeFormatHook),
        values.hoursHours[index] + (values.hoursMinutes[index] / 60),
      ),
      dateFormat,
      getTimeFormatHook
    );

    setValue(`endDate[${index}]`, nextEndDate);
  }

  const handleStartChange = (time) => {
    const next = setTime('start', time);
    if (values.type !== 'full') return changeEndDate(time);

    const copied = setTime('end', time, next);
    values.startDate[1] &&
      validateDatesOverlapping(copied, setError, clearErrors, getTimeFormatHook);
  };

  const handleEndChange = (time) => {
    const next = setTime('end', time);

    validateEndAfterStart(next, setError, clearErrors, getTimeFormatHook) &&
      values.startDate[1] &&
      validateDatesOverlapping(next, setError, clearErrors, getTimeFormatHook);
  };

  return (
    <Grid item xs={12} md={6} className={classes.zeroPaddingTop}>
      <label>
        <Typography variant="body1" color="textSecondary">
          {label}:
        </Typography>
        {/* Each part stays its own form field, registered while this row is shown. */}
        {parts.map(([, suffix]) => (
          <Controller key={suffix} name={`${name}${suffix}[${index}]`} control={control} />
        ))}
        <TimeInput
          className={classes.timeField}
          value={{
            hour: values[`${name}Hours`]?.[index],
            minute: values[`${name}Minutes`]?.[index],
            amPm: values[`${name}AmPm`]?.[index],
          }}
          onChange={name === 'end' ? handleEndChange : handleStartChange}
          error={Boolean(errors.startHours?.[index])}
        />
        {errors[`${name}Hours`] && errors[`${name}Hours`][index] ? (
          <div className={classes.error}>{errors[`${name}Hours`][index].message}</div>
        ) : null}
      </label>
    </Grid>
  );
};

export default TimeSelectForm;
